/**
 * bootstrapSlice — per-session bootstrap context for the React Calendar
 * Month View (Phase 1, Remediation 1, Task #2 / Blocker 2).
 *
 * Why this exists: per CLAUDE.md "USE SYMFONY API FOR NEW PAGES" + "NEVER
 * use SSR data", the Month View MUST NOT read its organization, user,
 * permissions, or timezone from `window.serverData`. Instead, it dispatches
 * `fetchBootstrap(orgUnique)` on mount; the thunk fans out to the Symfony
 * `userApi` and the existing `OrganizationApi.getOrganizationDashboard`
 * call in parallel and writes the resolved payload into this slice. Child
 * components (page header, CalendarNav bridge, EventModals bridge) read
 * from the slice once status === 'succeeded'.
 *
 * State shape mirrors the legacy Redux key set so the bridge wrapper in
 * `entrypoints/calendar-month-v2.js` can re-expose the data under the keys
 * `CalendarNav` and `EventModals` already expect (`organization`, `user`,
 * `accessgroups`, `timezone`).
 *
 * @module bootstrapSlice
 */

import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import OrganizationApi from '../../api/organizationApi.js';
import CalendarApi from '../../api/CalendarApi.js';
import {
	getUserProfile,
	getUserOrganizations,
	getUserSettings,
	getCalendarFeedStatus
} from '../../api/userApi.js';
import { unwrap } from './utils/apiEnvelope.js';
import { setApiUserFormats } from '../common/user-formats/userFormats.js';

// Re-export so existing test imports keep working — the helper now lives in
// `utils/apiEnvelope.js` (Phase 1, Remediation 2, Task #1) but
// `bootstrapSlice.test.js` imports `unwrap` from this module directly.
export { unwrap };

/**
 * @typedef {'idle'|'loading'|'succeeded'|'failed'} FetchStatus
 *
 * @typedef {Object} BootstrapState
 * @property {Object|null} organization
 * @property {Object|null} organizationSettings
 * @property {Object|null} accessGroups
 * @property {Object|null} user
 * @property {string|null} timezone
 * @property {Array<Object>} userOrgs
 * @property {FetchStatus} status
 * @property {string|null} error
 * @property {string|null} orgUnique  Last orgUnique we successfully bootstrapped against.
 */

/** @type {BootstrapState} */
export const initialState = {
	organization: null,
	organizationSettings: null,
	accessGroups: null,
	user: null,
	timezone: null,
	timezones: [],
	userOrgs: [],
	orgCalendarRights: {},
	// `userSettings` carries the per-user preference overrides surfaced by
	// `GET /user/settings` (notably `eventDefaultLength` in seconds, where 0
	// means "no override"). Non-blocking — `null` if the fetch failed.
	userSettings: null,
	// `calendarFeedStatus` is the current user's calendar-feed subscription
	// state (`{ state: 'none'|'pending'|'active', createdAt, lastFetchedAt,
	// fetchCount }`) from `GET /user/calendar-feed`. Non-blocking — `null` if
	// the fetch failed, which the Month View nav treats as "don't render the
	// subscribe control" rather than guessing a state.
	calendarFeedStatus: null,
	// `orgSettingsMap` is `{ [orgId: number]: { eventDefaultLength: int } }`
	// resolved from `POST /organizations/additional-data` for the orgs the
	// user can see in the calendar. Non-blocking — `{}` if the fetch failed.
	// CreateEventModal selects from this map by `formOrgUnique` so multi-org
	// Create Event respects each org's default event length.
	orgSettingsMap: {},
	// `eventOrgPerms` is `{ [orgUnique: string]: NormalizedAccessGroups }`
	// — Phase 3 cross-org permissions fan-out (Hybrid C). Populated by
	// `Promise.allSettled(getOrganizationDashboard(orgUnique))` for orgs the
	// user belongs to OTHER than the current URL-path org. Single-org users
	// pay nothing. Failed entries are dropped silently — `getEventOrgPerms`
	// returns `null` for missing orgs, which the EventViewModal uses to gate
	// Edit/Delete affordances until perms are known.
	eventOrgPerms: {},
	status: 'idle',
	error: null,
	orgUnique: null
};

/**
 * Normalize the Symfony timezone payload into the shape
 * `CalendarFilterBar` / `FilterTimezoneSelect` expects:
 * `[{ continent: string, zones: [{ id, label }] }]`.
 *
 * Accepts several upstream shapes observed across controllers:
 *   - `[{ name, timeZones: [{ id, name, offsetString, abbr }] }]`
 *   - `{ [continent]: { timeZones: [...] } }`
 *   - `[{ continent, zones: [{ id, label }] }]` (already canonical)
 *
 * Pure helper, exported for tests.
 */
export function normalizeTimezonesPayload(raw) {
	if (!raw) return [];

	const buildZone = z => {
		if (!z) return null;
		const id = z.id || z.name || null;
		if (!id) return null;
		const labelParts = [z.offsetString, z.abbr, z.label || z.name].filter(Boolean);
		const label = labelParts.length ? labelParts.join(' ') : String(id);
		return { id: String(id), label };
	};

	const buildGroup = (continent, zones) => {
		const mapped = (zones || []).map(buildZone).filter(Boolean);
		if (mapped.length === 0) return null;
		return { continent: String(continent || 'Other'), zones: mapped };
	};

	if (Array.isArray(raw)) {
		return raw
			.map(group => {
				if (!group) return null;
				const continent = group.continent || group.name || 'Other';
				const zones = group.zones || group.timeZones || [];
				return buildGroup(continent, zones);
			})
			.filter(Boolean);
	}

	if (typeof raw === 'object') {
		return Object.entries(raw)
			.map(([continent, group]) => {
				const zones = (group && (group.zones || group.timeZones)) || [];
				return buildGroup(continent, zones);
			})
			.filter(Boolean);
	}

	return [];
}

/**
 * Pick a usable timezone string from a profile payload. Handles every
 * shape we have observed in the wild:
 *   - `profile.timezone` (camelCase, primitive string)
 *   - `profile.timeZone` (capital Z, what `UserProfileResponseDto` uses)
 *   - either of the above as a `TimeZoneDto` object → `{ id, name, offset }`
 *   - the same fields nested under `profile.user`
 *
 * Returns `null` when nothing matches so the consumer can fall back to
 * `Intl.DateTimeFormat().resolvedOptions().timeZone` if needed.
 *
 * @internal exported for unit tests
 */
export function pickTimezone(profile) {
	if (!profile || typeof profile !== 'object') return null;
	const candidates = [
		profile.timezone,
		profile.timeZone,
		profile.user && profile.user.timezone,
		profile.user && profile.user.timeZone
	];
	for (let i = 0; i < candidates.length; i += 1) {
		const c = candidates[i];
		if (c == null) continue;
		if (typeof c === 'string' && c.length > 0) return c;
		if (typeof c === 'object') {
			// TimeZoneDto: prefer `.id` (Olson tz like "America/Denver"), fall
			// back to `.name`. Skip otherwise.
			if (typeof c.id === 'string' && c.id.length > 0) return c.id;
			if (typeof c.name === 'string' && c.name.length > 0) return c.name;
		}
	}
	return null;
}

/**
 * fetchBootstrap — orchestrates the parallel bootstrap fetch.
 *
 * Runs `getUserProfile`, `OrganizationApi.getOrganizationDashboard`, and
 * `getUserOrganizations` simultaneously. On any rejection the thunk rejects
 * with the first error so the page can show a single retry button rather
 * than three competing error UIs.
 *
 * @returns {Promise<{
 *   organization: Object,
 *   organizationSettings: Object,
 *   accessGroups: Object,
 *   user: Object,
 *   timezone: string,
 *   userOrgs: Array<Object>
 * }>}
 */
export const fetchBootstrap = createAsyncThunk(
	'calendarMonthBootstrap/fetch',
	async (orgUnique, { rejectWithValue }) => {
		if (!orgUnique) {
			return rejectWithValue('Missing organization slug in URL');
		}
		try {
			// `getUserOrganizations()` is kept ONLY for
			// `organizationCalendarManageAnothersRights` — the list of orgs in
			// the calendar filter must come from `CalendarApi.listOrganizations`
			// so it's pre-filtered by VIEW_CALENDAR permission (matches what
			// the events endpoint restricts to). Without this split, local dev
			// saw 3 orgs but prod/dev saw 2 because only 2 had the permission.
			// We intentionally DO NOT `.catch` the calendar orgs call any more —
			// if it fails we want the whole bootstrap to reject so the user
			// sees the error UI rather than silently getting the wrong 3-org
			// list from the legacy `getUserOrganizations` fallback.
			const [
				profileRes,
				dashboardRes,
				rightsRes,
				calendarOrgsRes,
				timezonesRes,
				userSettingsRes,
				calendarFeedStatusRes
			] = await Promise.all([
				getUserProfile(),
				OrganizationApi.getOrganizationDashboard(orgUnique),
				getUserOrganizations(),
				CalendarApi.listOrganizations(),
				CalendarApi.getTimezones().catch(() => null),
				// Non-blocking: per-user override is preference data — failing
				// the whole bootstrap on a settings flake is poor UX. Selector
				// falls through to org-level when this is `null`.
				getUserSettings().catch(() => null),
				// Non-blocking: the Month View nav must load even if the
				// subscribe control's state can't be determined — it just
				// hides the control instead.
				getCalendarFeedStatus().catch(() => null)
			]);
			// `unwrap()` strips the double Symfony envelope and throws on
			// `success: false`, so a backend rejection lands in the catch
			// block below as a regular Error.
			const profile = unwrap(profileRes) || {};
			const dashboard = unwrap(dashboardRes) || {};
			const rightsPayload = unwrap(rightsRes) || {};
			const calendarOrgsPayload = unwrap(calendarOrgsRes);

			// Symfony dashboard endpoint returns the Organization entity directly
			// (not wrapped in `{ organization: ... }`). Detect by checking for `id`.
			const org = dashboard.organization || (dashboard.id ? dashboard : null);
			const settings =
				(org && org.organizationSettings) ||
				dashboard.organizationSettings ||
				dashboard.settings ||
				null;
			// Nest for legacy CalendarNav compatibility — it reads
			// `organization.organizationSettings.enableResourceView` at
			// calendar.nav.js:134-138.
			if (org && settings) {
				org.organizationSettings = settings;
			}

			// Share the `userMenu` from this SAME dashboard payload so the global
			// breadcrumb (`HeroUIBreadcrumb`, which reads `state.dashboard`) does
			// NOT fire a second `/org/{unique}/dashboard` request on the calendar
			// page. Mirror `fetchUserMenu`'s key fallbacks (backend returns
			// `organizationMenu`, frontend uses `userMenu`).
			const userMenu =
				dashboard.userMenu ||
				dashboard.organizationMenu ||
				dashboard.data?.userMenu ||
				dashboard.data?.organizationMenu ||
				[];

			// Timezones endpoint is non-blocking: failure (`null` via .catch above)
			// leaves the filter bar with an empty list, which is still functional.
			const timezonesPayload = timezonesRes ? unwrap(timezonesRes) : null;
			const timezones = normalizeTimezonesPayload(
				timezonesPayload && typeof timezonesPayload === 'object' && timezonesPayload.timezones
					? timezonesPayload.timezones
					: timezonesPayload
			);

			// User settings — non-blocking. Wrap unwrap() in try/catch because
			// unwrap throws on `success: false`, which we don't want to bubble
			// up for an optional preference fetch.
			let userSettings = null;
			if (userSettingsRes) {
				try {
					userSettings = unwrap(userSettingsRes) || null;
				} catch (_) {
					userSettings = null;
				}
			}
			if (userSettings) {
				setApiUserFormats(userSettings);
			}

			// Calendar feed subscription status — non-blocking, same pattern as
			// `userSettings` above. `null` here means the Month View nav hides
			// the subscribe control rather than rendering a guessed state.
			let calendarFeedStatus = null;
			if (calendarFeedStatusRes) {
				try {
					calendarFeedStatus = unwrap(calendarFeedStatusRes) || null;
				} catch (_) {
					calendarFeedStatus = null;
				}
			}

			// Phase 2 — fan out per-org additional-data using IDs from the
			// already-resolved calendar orgs payload. Non-blocking; failure
			// leaves `orgSettingsMap` empty and the selector falls through.
			const userOrgsForIds = Array.isArray(calendarOrgsPayload)
				? calendarOrgsPayload
				: calendarOrgsPayload && Array.isArray(calendarOrgsPayload.organizations)
					? calendarOrgsPayload.organizations
					: [];
			const orgIds = userOrgsForIds
				.map(o => Number(o?.id))
				.filter(n => Number.isFinite(n) && n > 0);
			let additionalDataPayload = null;
			if (orgIds.length > 0) {
				try {
					const additionalDataRes = await OrganizationApi.getOrganizationsAdditionalData(
						orgIds
					).catch(() => null);
					if (additionalDataRes) {
						additionalDataPayload = unwrap(additionalDataRes);
					}
				} catch (_) {
					additionalDataPayload = null;
				}
			}
			const orgSettingsMap = {};
			if (additionalDataPayload && typeof additionalDataPayload === 'object') {
				for (const [orgId, dto] of Object.entries(additionalDataPayload)) {
					const v = dto?.settings?.eventDefaultLength;
					const n = Number(v);
					if (Number.isFinite(n)) {
						orgSettingsMap[Number(orgId)] = { eventDefaultLength: n };
					}
				}
			}

			// Phase 3 — cross-org permissions fan-out REMOVED. Multi-org was
			// dropped from the calendar, so only the current org's events are
			// ever shown and `getEventOrgPerms` always resolves via the current
			// org's `accessGroups` (branch 1). The old fan-out prefetched every
			// OTHER org's `/org/{unique}/dashboard` for cross-org Edit/Delete
			// perms — now dead, and it was duplicating the current-org dashboard
			// request N times (the `getOrganizationDashboard(u)` helper ignored
			// `u`), so removing it also eliminates those redundant requests.
			// Kept as an empty map so the selector/reducer still fall back to
			// `null` (→ current-org perms) without any extra change.
			const eventOrgPerms = {};

			return {
				organization: org,
				organizationSettings: settings,
				// Consumed by the `dashboard` reducer's bootstrap-fulfilled case so
				// the breadcrumb menu renders without its own fetch (see Part 2).
				userMenu,
				accessGroups:
					(org && org.accessGroups) ||
					dashboard.accessGroups ||
					dashboard.accessgroups ||
					dashboard.permissions ||
					null,
				user: profile.user || profile,
				timezone: pickTimezone(profile),
				timezones,
				// `userOrgs` is ONLY sourced from `/api/v1/calendar/organizations`
				// (permission-filtered by VIEW_CALENDAR). No legacy fallback —
				// if the calendar endpoint fails, the thunk rejects above.
				userOrgs: Array.isArray(calendarOrgsPayload)
					? calendarOrgsPayload
					: calendarOrgsPayload && Array.isArray(calendarOrgsPayload.organizations)
						? calendarOrgsPayload.organizations
						: [],
				orgCalendarRights: rightsPayload.organizationCalendarManageAnothersRights || {},
				userSettings,
				orgSettingsMap,
				eventOrgPerms,
				calendarFeedStatus
			};
		} catch (err) {
			// Log the raw error for on-call / Sentry, but surface a user-safe
			// message to the UI. Without this, JS runtime errors like
			// "X is not a function" leak verbatim into `BootstrapError`.
			// eslint-disable-next-line no-console
			console.error('[fetchBootstrap]', err);
			const backendMessage = err?.response?.data?.error?.[0];
			const isJsRuntime =
				err instanceof TypeError || err instanceof ReferenceError || err instanceof SyntaxError;
			const message = backendMessage
				? backendMessage
				: isJsRuntime
					? "We couldn't open the calendar. Please reload the page."
					: err?.message || 'Failed to load Month View.';
			return rejectWithValue(message);
		}
	}
);

/**
 * refreshCalendarFeedStatus — re-fetches just the subscribe control's
 * subscription state, so the Month View nav can reflect a subscribe/rotate
 * action immediately without re-running the whole bootstrap fan-out or
 * reloading the page. On failure the existing `calendarFeedStatus` is left
 * untouched (no reducer case below handles `.rejected`) — a transient error
 * refreshing an already-visible control shouldn't hide it.
 */
export const refreshCalendarFeedStatus = createAsyncThunk(
	'calendarMonthBootstrap/refreshCalendarFeedStatus',
	async (_, { rejectWithValue }) => {
		try {
			return unwrap(await getCalendarFeedStatus()) || null;
		} catch (err) {
			return rejectWithValue(err?.message || 'Failed to refresh calendar feed status');
		}
	}
);

const bootstrapSlice = createSlice({
	name: 'calendarMonthBootstrap',
	initialState,
	reducers: {
		resetBootstrap() {
			return initialState;
		}
	},
	extraReducers: builder => {
		builder
			.addCase(fetchBootstrap.pending, (state, action) => {
				state.status = 'loading';
				state.error = null;
				state.orgUnique = action.meta.arg || null;
			})
			.addCase(fetchBootstrap.fulfilled, (state, action) => {
				state.status = 'succeeded';
				state.error = null;
				state.organization = action.payload.organization;
				state.organizationSettings = action.payload.organizationSettings;
				state.accessGroups = action.payload.accessGroups;
				state.user = action.payload.user;
				state.timezone = action.payload.timezone;
				state.timezones = action.payload.timezones || [];
				state.userOrgs = action.payload.userOrgs;
				state.orgCalendarRights = action.payload.orgCalendarRights;
				state.userSettings = action.payload.userSettings || null;
				state.orgSettingsMap = action.payload.orgSettingsMap || {};
				state.eventOrgPerms = action.payload.eventOrgPerms || {};
				state.calendarFeedStatus = action.payload.calendarFeedStatus || null;
			})
			.addCase(fetchBootstrap.rejected, (state, action) => {
				state.status = 'failed';
				state.error = action.payload || action.error?.message || 'Bootstrap failed';
			})
			.addCase(refreshCalendarFeedStatus.fulfilled, (state, action) => {
				state.calendarFeedStatus = action.payload;
			});
	}
});

export const { resetBootstrap } = bootstrapSlice.actions;
export default bootstrapSlice.reducer;
